import {
  type GraphLike,
  buildDecisionReportTree,
  orderedSuccessors,
} from "@/components/DecisionReport/buildDecisionReportTree";
import { getNodeResult } from "@imminently/decisively-core";
import { normalizeEvent } from "@packages/commons";
import { useCallback, useEffect, useRef } from "react";
import type { CardDataKind, CardDataOptions } from "./cardDataKinds";

/** Session graph surface the data source reads: node ids, node lookup, and successors. */
export type CardSessionGraph = GraphLike & { nodes(): string[] };

/** The session graph as served to cards: nodes keyed by graph key, and edges as [from, to] key pairs. */
export interface CardSessionGraphData {
  nodes: Record<string, Record<string, unknown>>;
  edges: Array<[string, string]>;
}

/** What the host page has loaded for the viewed session. Values can still be loading (null/undefined). */
export interface CardDataInputs {
  session: Record<string, unknown> | null | undefined;
  sessionGraph: CardSessionGraph | null | undefined;
  /** The release rule graph in its stored form (`release.rule_graph`). */
  ruleGraph: unknown;
  sessionGoal?: string;
  canReadContacts: boolean;
  loadContacts(session: Record<string, unknown>): Promise<unknown[]>;
  /** Every event recorded against the session, as stored (raw, in any order). */
  loadEvents(sessionId: string): Promise<unknown[]>;
  /** One interaction with its stored outcome, request and response merged in. */
  loadInteraction(sessionId: string, interactionId: string): Promise<unknown>;
}

type Interaction = Record<string, unknown>;

/** Whether an interaction's outcome, request and response are held in storage rather than on the session record. */
function hasStoredData(interaction: Interaction): boolean {
  return interaction.hasStoredData === true || typeof interaction.outcomeFile === "string";
}

/**
 * An interaction as served to cards. The storage key (`outcomeFile`) is internal, so it is replaced by the one
 * documented flag: `hasStoredData: true` means request the interaction by id to get its stored content.
 */
function toCardInteraction(interaction: Interaction): Interaction {
  if (!hasStoredData(interaction)) return interaction;
  const { outcomeFile: _outcomeFile, ...rest } = interaction;
  return { ...rest, hasStoredData: true };
}

/**
 * Events as served to cards: normalised to one stable shape (stored events vary by age), oldest first so they read as
 * a timeline. Events with no timestamp go last.
 */
export function toCardEvents(raw: unknown[]): Array<Record<string, unknown>> {
  const events = raw
    .map(
      (event) => normalizeEvent(event as Parameters<typeof normalizeEvent>[0]) as Record<string, unknown> | undefined,
    )
    .filter((event): event is Record<string, unknown> => Boolean(event));
  const time = (event: Record<string, unknown>) => (typeof event.timestamp === "string" ? event.timestamp : "\uffff");
  return events.sort((a, b) => time(a).localeCompare(time(b)));
}

/** The session record as served to cards: no graph (served separately), and interactions in their card form. */
export function toCardSession(session: Record<string, unknown>): Record<string, unknown> {
  const { graph: _graph, ...rest } = session;
  if (!Array.isArray(rest.interactions)) return rest;
  return { ...rest, interactions: (rest.interactions as Interaction[]).map(toCardInteraction) };
}

/** Resolves a card's `decisively.data.get(kind, options)` request. */
export type CardDataGetter = (kind: CardDataKind, options?: CardDataOptions) => Promise<unknown>;

/** How long a request waits for the page to finish loading what it needs before failing. */
const READY_TIMEOUT_MS = 60_000;

function fail(code: string): Error & { code: string } {
  return Object.assign(new Error(code), { code });
}

/** Rule-authoring fields on a session graph node. They define how a value is derived, not the result, and are in the rule graph. */
const RULE_DETAIL_KEYS = new Set(["conditions", "otherwise", "rows", "columns", "value"]);

/** Flat map of session graph key to result (`input`, else `derived`), skipping hidden and unset nodes. */
export function getAttributeValues(graph: CardSessionGraph): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const key of graph.nodes()) {
    const node = graph.node(key);
    if (!node || node.hidden === true) continue;
    // `node.value` is the rule expression; the result is the input if one was given, otherwise the derived value.
    const result = getNodeResult(node as Parameters<typeof getNodeResult>[0]);
    if (result !== undefined) values[key] = result;
  }
  return values;
}

/**
 * Flat copy of the session graph: hidden nodes (and edges touching them) left out, rule-authoring detail stripped.
 * Each node's edges are listed in decision report order, so a card walking them sees children as the report does.
 */
export function getSessionGraphData(graph: CardSessionGraph): CardSessionGraphData {
  const nodes: CardSessionGraphData["nodes"] = {};
  for (const key of graph.nodes()) {
    const node = graph.node(key);
    if (!node || node.hidden === true) continue;
    // Shallow on purpose: an input or derived value may legitimately contain a `value` key of its own.
    nodes[key] = Object.fromEntries(Object.entries(node).filter(([field]) => !RULE_DETAIL_KEYS.has(field)));
  }
  const edges: CardSessionGraphData["edges"] = [];
  for (const from of Object.keys(nodes)) {
    for (const to of orderedSuccessors(graph, from)) if (Object.hasOwn(nodes, to)) edges.push([from, to]);
  }
  return { nodes, edges };
}

/**
 * Data source for data bridge v2. Each kind is resolved only when a card asks for it, so the decision report is never
 * built unless requested. Requests made before the page has loaded what they need wait for it, up to READY_TIMEOUT_MS.
 */
export function useCardDataSource(inputs: CardDataInputs): CardDataGetter {
  const latest = useRef(inputs);
  latest.current = inputs;
  const waiters = useRef(new Set<() => void>());

  useEffect(() => {
    for (const check of [...waiters.current]) check();
  }, [inputs.session, inputs.sessionGraph, inputs.ruleGraph]);

  useEffect(() => {
    const pending = waiters.current;
    return () => pending.clear();
  }, []);

  const when = useCallback(<T>(pick: (current: CardDataInputs) => T | null | undefined): Promise<T> => {
    const ready = pick(latest.current);
    if (ready !== null && ready !== undefined) return Promise.resolve(ready);
    return new Promise((resolve, reject) => {
      const check = (): void => {
        const value = pick(latest.current);
        if (value === null || value === undefined) return;
        clearTimeout(timer);
        waiters.current.delete(check);
        resolve(value);
      };
      const timer = setTimeout(() => {
        waiters.current.delete(check);
        reject(fail("DATA_UNAVAILABLE"));
      }, READY_TIMEOUT_MS);
      waiters.current.add(check);
    });
  }, []);

  return useCallback(
    async (kind: CardDataKind, options: CardDataOptions = {}): Promise<unknown> => {
      switch (kind) {
        case "session":
          return toCardSession(await when((current) => current.session));
        case "attributeValues":
          return getAttributeValues(await when((current) => current.sessionGraph));
        case "sessionGraph":
          return getSessionGraphData(await when((current) => current.sessionGraph));
        case "contacts": {
          if (!latest.current.canReadContacts) throw fail("FORBIDDEN");
          const session = await when((current) => current.session);
          return latest.current.loadContacts(session);
        }
        case "ruleGraph":
          return when((current) => current.ruleGraph);
        case "decisionReport": {
          const graph = await when((current) => current.sessionGraph);
          const goal = latest.current.sessionGoal;
          return goal ? buildDecisionReportTree(graph, goal) : [];
        }
        case "interaction": {
          const session = await when((current) => current.session);
          const list = Array.isArray(session.interactions) ? (session.interactions as Interaction[]) : [];
          const interaction = list.find((item) => item?.id === options.id);
          // Only interactions on this session can be fetched, so an unknown id never reaches the API.
          if (!interaction || typeof session.id !== "string") throw fail("NOT_FOUND");
          if (!hasStoredData(interaction)) return toCardInteraction(interaction);
          return toCardInteraction(
            (await latest.current.loadInteraction(session.id, options.id as string)) as Interaction,
          );
        }
        case "events": {
          const session = await when((current) => current.session);
          if (typeof session.id !== "string") throw fail("NOT_FOUND");
          return toCardEvents(await latest.current.loadEvents(session.id));
        }
      }
    },
    [when],
  );
}
